import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { OrderDto } from '@/types/dtos/orders/response/order.dto.ts';
import { GET_EVENT_ORDERS_QUERY_KEY } from '@/state/queries/orders/use-get-event-orders.ts';
import type { UpdateOrderDto } from '@/types/dtos/orders/request/update-order.dto.ts';
import { GET_EVENTS_QUERY_KEY } from '@/state/queries/events/use-events.ts';
import { enrichPatchOrderLineItemsWithCatalogAutoAssign } from '@/helpers/order-line-item.helper.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

export const useUpdateOrderMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { hasAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);

  return useMutation({
    mutationFn: async (orderDto: UpdateOrderDto) => {
      if (!hasAccess) throw new Error('Event catalog orders are not available');
      const token = await getToken();
      return updateOrder(orderDto, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_ORDERS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENTS_QUERY_KEY],
        }),
      ]);
    },
  });
};

const updateOrder = async (orderDto: UpdateOrderDto, token: string): Promise<OrderDto> => {
  const { orderId, lineItems, ...rest } = orderDto;
  const body =
    lineItems !== undefined
      ? {
          ...rest,
          lineItems: enrichPatchOrderLineItemsWithCatalogAutoAssign(lineItems, useProductsStore.getState().products),
        }
      : rest;
  const url = `${import.meta.env.VITE_API_URL}/orders/${orderId}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body, token });
};

export default useUpdateOrderMutation;
